import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  SheetHeader,
  SheetTitle,
  usePreventSheetClose,
} from "@/components/ui/sheet";
import { SheetBackButton } from "@common/components/SheetActions/SheetBackButton";
import {
  AdminCustomFieldsDocument,
  CustomFieldEntity,
  CustomFieldEntityState,
  CustomFieldListOption,
  useUpsertCustomFieldMutation,
} from "@common/utils/graphql";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import {
  AlertDialogContent,
  AlertDialogTitle,
  AlertDialog,
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
} from "@/components/ui/alert-dialog";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { isListField } from "@common/components/CustomFields";
import { hasDirtyFields } from "@/utils/utils";

const hasVisibleOptions = (options?: CustomFieldListOption[] | null) => {
  return options && options.some((option) => !option.isDeleted);
};

const stateOptions = [
  {
    value: CustomFieldEntityState.Optional,
    hint: "A value is not required to sumbit an expense",
  },
  {
    value: CustomFieldEntityState.Required,
    hint: "The field must be completed to submit an expense",
  },
  {
    value: CustomFieldEntityState.Hidden,
    hint: "The field is not visble to employees",
  },
];

const FormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  state: z
    .enum([
      CustomFieldEntityState.Hidden,
      CustomFieldEntityState.Optional,
      CustomFieldEntityState.Required,
      CustomFieldEntityState.Deleted,
    ])
    .default(CustomFieldEntityState.Optional),
});

type FormValues = z.infer<typeof FormSchema>;

type Props = {
  customField: CustomFieldEntity;
  onBack?: () => void;
  onClose?: () => void;
};
const loading = false;
export const EditCustomFieldForm = ({
  customField,
  onBack,
  onClose,
}: Props) => {
  const { toast } = useToast();
  const [isPreventEnablingDialogOpen, setIsPreventEnablingDialogOpen] =
    useState(false);
  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      name: customField.name,
      state: customField.state as CustomFieldEntityState,
    },
  });
  const [upsertCustomField] = useUpsertCustomFieldMutation({
    refetchQueries: [AdminCustomFieldsDocument],
  });

  usePreventSheetClose(() => hasDirtyFields(form));

  const onSubmit = async (values: FormValues) => {
    // Prevent enabling custom list field with no visible options
    if (
      values.state !== CustomFieldEntityState.Hidden &&
      isListField(customField) &&
      !hasVisibleOptions(customField.listOptions)
    ) {
      setIsPreventEnablingDialogOpen(true);
      return;
    }
    try {
      const { data } = await upsertCustomField({
        variables: {
          input: {
            name: values.name,
            type: customField.type,
            state: values.state,
            uuid: customField.uuid,
          },
        },
      });
      if (
        data?.upsertCustomField.__typename !==
        "MutationUpsertCustomFieldSuccess"
      ) {
        throw new Error();
      }
      toast({
        title: `${customField.name} has been updated successfully.`,
      });

      // Only one of onBack or onClose will be defined
      onBack?.();
      onClose?.();
    } catch (e) {
      toast({
        title: `Failed to update ${customField.name}`,
        description: `An error occurred while updating ${customField.name}. Please try again.`,
        variant: "destructive",
      });
    }
  };
  return (
    <>
      {onBack && <SheetBackButton onClick={onBack} />}
      <SheetHeader>
        <SheetTitle>Edit Custom Field</SheetTitle>
      </SheetHeader>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex grow flex-col space-y-4 px-sheet-gap pb-sheet-gap"
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Name</FormLabel>
                <FormControl>
                  <Input placeholder="Name" disabled={loading} {...field} />
                </FormControl>
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="state"
            render={({ field }) => (
              <FormItem>
                <FormLabel>State</FormLabel>
                <FormControl>
                  <RadioGroup onValueChange={field.onChange} {...field}>
                    {stateOptions.map((state) => (
                      <div className="flex gap-x-2">
                        <RadioGroupItem
                          key={`custom-field-state-option--${state.value}`}
                          id={`custom-field-state-option--${state.value}`}
                          aria-describedby={`custom-field-state-option-hint--${state.value}`}
                          value={state.value}
                        />
                        <div>
                          <Label
                            htmlFor={`custom-field-state-option--${state.value}`}
                            className="text-body-md capitalize"
                          >
                            {state.value}
                          </Label>
                          <span
                            className="block text-body-sm font-medium text-white/50"
                            id={`custom-field-state-option-hint--${state.value}`}
                          >
                            {state.hint}
                          </span>
                        </div>
                      </div>
                    ))}
                  </RadioGroup>
                </FormControl>
              </FormItem>
            )}
          />
          <div className="flex w-full grow items-end">
            <Button type="submit" size="lg" className="w-full">
              Save
            </Button>
          </div>
        </form>
      </Form>
      {/* Alert dialog for custom field with no visible options */}
      <AlertDialog open={isPreventEnablingDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cannot Enable Custom Field</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogDescription>
            No visible options are configured for this custom field. Please
            return to the previous screen and add an option to enable this
            custom field.
          </AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button onClick={() => setIsPreventEnablingDialogOpen(false)}>
                OK
              </Button>
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
